import { EMPTY_STRING } from '@rotomeca/utils';
import BnumElement, { CustomDom } from '../../bnum-element';
import { HTMLBnumCardItem } from '../card-item/bnum-card-item';
import { TAG_CARD_LIST } from '../../../core/utils/constants/tags';
import style from './bnum-card-list.less';
import { Nullable } from '@rotomeca/utils';
import { SchedulerArray } from '../../../core/utils/Scheduler';
import { Define, SetAttr } from '../../../core/decorators';

//#region Global Constants
const SYMBOL_RESET = Symbol('reset');
//#endregion Global Constants

/**
 * Composant liste de cartes Bnum.
 *
 * Permet d'afficher une liste d'éléments de type carte.
 *
 * @category Card
 *
 * @structure Default
 * <bnum-card-list>
 *  <bnum-card-item></bnum-card-item>
 *  <bnum-card-item></bnum-card-item>
 *  <bnum-card-item></bnum-card-item>
 * </bnum-card-list>
 *
 * @structure Mail et agenda
 * <bnum-card-list>
 *   <bnum-card-item-mail data-date="now">
 *     <span slot="subject">Sujet par défaut</span>
 *     <span slot="sender">Expéditeur par défaut</span>
 *   </bnum-card-item-mail>
 * <bnum-card-item-agenda
 *    data-date="2025-11-20"
 *    data-start-date="2025-10-20 09:40:00"
 *    data-end-date="2025-12-20 10:10:00"
 *    data-title="Réunion de projet"
 *    data-location="Salle de conférence">
 * </bnum-card-item-agenda>
 * </bnum-card-list>
 *
 * @structure Dans une card
 * <bnum-card>
 * <bnum-card-title slot="title" data-icon="info">Diverses informations</bnum-card-title>
 * <bnum-card-list>
 *   <bnum-card-item-mail data-date="now">
 *     <span slot="subject">Sujet par défaut</span>
 *     <span slot="sender">Expéditeur par défaut</span>
 *   </bnum-card-item-mail>
 * <bnum-card-item-agenda
 *    data-date="2025-11-20"
 *    data-start-date="2025-10-20 09:40:00"
 *    data-end-date="2025-12-20 10:10:00"
 *    data-title="Réunion de projet"
 *    data-location="Salle de conférence">
 * </bnum-card-item-agenda>
 * </bnum-card-list>
 * </bnum-card>
 *
 * @slot (default) - Contenu de la liste de cartes (éléments HTMLBnumCardItem)
 *
 *
 */
@Define({ tag: TAG_CARD_LIST, styles: style })
export class HTMLBnumCardList extends BnumElement {
	//#region Private fields
	/**
	 * Ordonnanceur de modifications de la liste.
	 */
	#_modifierScheduler: Nullable<SchedulerArray<HTMLBnumCardItem>> = null;
	//#endregion Private fields

	//#region Lifecycle
	/**
	 * Constructeur de la liste de cartes.
	 */
	constructor() {
		super();
	}

	/**
	 * Construit le DOM interne du composant.
	 * @param container Racine du shadow DOM ou élément HTML
	 */
	@SetAttr('role', 'list')
	protected _p_buildDOM(container: CustomDom): void {
		super._p_buildDOM(container);
		container.appendChild(this._p_createSlot());
	}
	//#endregion Lifecycle

	//#region Public methods
	/**
	 * Ajoute un ou plusieurs éléments de type carte à la liste.
	 * @param nodes Éléments HTMLBnumCardItem à ajouter
	 * @returns {this} L'instance courante
	 */
	public add(...nodes: HTMLBnumCardItem[]): this {
		return this.#_requestModifier(nodes);
	}

	/**
	 * Vide la liste de toutes ses cartes.
	 * @returns {this} L'instance courante
	 */
	public clear(): this {
		return this.#_requestModifier(SYMBOL_RESET);
	}
	//#endregion Public methods
	//#region  Private methods
	#_requestModifier(items: HTMLBnumCardItem[] | symbol): this {
		(this.#_modifierScheduler ??= new SchedulerArray<HTMLBnumCardItem>(
			values => this.#_modifier(values!),
			SYMBOL_RESET,
		)).schedule(items);

		return this;
	}
	#_modifier(items: HTMLBnumCardItem[] | symbol): void {
		if (items === SYMBOL_RESET) {
			this.innerHTML! = EMPTY_STRING;
		} else this.append(...(items as HTMLBnumCardItem[]));
	}
	//#endregion  Private methods

	//#region Static methods
	/**
	 * Crée une nouvelle instance de liste de cartes avec des éléments optionnels.
	 * @param items Tableau d'éléments HTMLBnumCardItem ou null
	 * @returns {HTMLBnumCardList} Nouvelle instance de liste de cartes
	 */
	static Create(items: Nullable<HTMLBnumCardItem[]> = null): HTMLBnumCardList {
		const node = document.createElement(this.TAG) as HTMLBnumCardList;

		if (items && items.length > 0) {
			node.add(
				...items.filter((item): item is HTMLBnumCardItem => item !== null),
			);
		}

		return node;
	}
	//#endregion Static methods
}
